import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { TierRow, TypeTier } from '@/lib/database.types';

export interface TiersFilter {
  entrepriseId?: string | null;
  type?: TypeTier | null;
  types?: TypeTier[] | null;
  activiteId?: string | null;
  search?: string;
  active?: boolean | null;
}

export function useTier(id: string | null) {
  return useQuery({
    queryKey: ['tier', id],
    enabled: !!id,
    queryFn: async (): Promise<TierRow | null> => {
      if (!id) return null;
      const { data, error } = await supabase.from('tiers').select('*').eq('id', id).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

export function useTiers(filter: TiersFilter) {
  return useQuery({
    queryKey: ['tiers', filter],
    enabled: !!filter.entrepriseId,
    queryFn: async (): Promise<TierRow[]> => {
      let q = supabase
        .from('tiers')
        .select('*')
        .order('raison_sociale', { ascending: true })
        .limit(2000);
      if (filter.entrepriseId) q = q.eq('entreprise_id', filter.entrepriseId);
      if (filter.type) q = q.eq('type', filter.type);
      if (filter.types && filter.types.length) q = q.in('type', filter.types);
      if (filter.activiteId) q = q.eq('activite_principale_id', filter.activiteId);
      if (filter.active !== null && filter.active !== undefined)
        q = q.eq('is_active', filter.active);
      if (filter.search && filter.search.length >= 2) {
        const s = filter.search.replace(/%/g, '');
        q = q.or(
          `raison_sociale.ilike.%${s}%,nom.ilike.%${s}%,prenom.ilike.%${s}%,email.ilike.%${s}%,telephone.ilike.%${s}%,code.ilike.%${s}%`,
        );
      }
      const { data, error } = await q;
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertTierInput {
  id?: string;
  entreprise_id: string;
  type: TypeTier;
  code?: string | null;
  raison_sociale: string;
  nom?: string | null;
  prenom?: string | null;
  email?: string | null;
  telephone?: string | null;
  ville?: string | null;
  wilaya?: string | null;
  pays?: string;
  nif?: string | null;
  rc?: string | null;
  adresse?: string | null;
  encours_max?: number | null;
  notes?: string | null;
  activite_principale_id?: string | null;
  is_active?: boolean;
}

export function useUpsertTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertTierInput): Promise<TierRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('tiers')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('tiers').insert(rest).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tiers'] });
    },
  });
}

export function useDeleteTier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('tiers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tiers'] });
    },
  });
}

export function useBulkDeleteTiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      ids: string[],
    ): Promise<{ success: string[]; errors: { id: string; message: string }[] }> => {
      const result = { success: [] as string[], errors: [] as { id: string; message: string }[] };
      const { error, data } = await supabase.from('tiers').delete().in('id', ids).select('id');
      if (error) {
        for (const id of ids) result.errors.push({ id, message: error.message });
      } else {
        for (const r of data ?? []) result.success.push(r.id);
      }
      return result;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tiers'] }),
  });
}

export function useBulkUpdateTierActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; is_active: boolean }): Promise<void> => {
      const { error } = await supabase
        .from('tiers')
        .update({ is_active: input.is_active })
        .in('id', input.ids);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['tiers'] }),
  });
}

export interface TierDoublonGroup {
  entreprise_id: string;
  nom_normalise: string;
  tier_ids: string[];
  raisons_sociales: string[];
  nb_doublons: number;
}

export function useTierDoublons(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['tier_doublons', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<TierDoublonGroup[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('v_tier_doublons')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('nb_doublons', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as TierDoublonGroup[];
    },
  });
}

export function useMergeTiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      master_id: string;
      slave_ids: string[];
    }): Promise<{
      master_id: string;
      slave_ids: string[];
      nb_slaves_supprimes: number;
      nb_operations_reaffectees: number;
    }> => {
      const { data, error } = await supabase.rpc('tiers_merge', {
        p_master_id: input.master_id,
        p_slave_ids: input.slave_ids,
      });
      if (error) throw error;
      return data as unknown as {
        master_id: string;
        slave_ids: string[];
        nb_slaves_supprimes: number;
        nb_operations_reaffectees: number;
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tiers'] });
      void qc.invalidateQueries({ queryKey: ['tier_doublons'] });
      void qc.invalidateQueries({ queryKey: ['operations'] });
    },
  });
}

export function useBulkInsertTiers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: UpsertTierInput[]): Promise<TierRow[]> => {
      const { data, error } = await supabase.from('tiers').insert(rows).select('*');
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tiers'] });
    },
  });
}
